import { useEffect, useState } from "react";
import { IRoentgenModalProps } from "../types/modal";

import { useAppDispatch } from "../hooks/redux";
import { closeModalById, openModal } from "../features/modals";

import { useFormik } from "formik";
import axios from "axios";

import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  DialogContent,
  TextField,
  Stack,
  Autocomplete,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import roentgenSchema, { RoentgenBase } from "../schemas/roentgen";
import PermissionGuard from "../components/PermissionGuard";
import PERMISSIONS from "../constants/permissions";
import MODALS from "../constants/modals";
import { ClinicResponse } from "../types/clinic";

const RoentgenModal: React.FunctionComponent<IRoentgenModalProps> = ({
  id,
  callback,
  initialValues,
}) => {
  const dispatch = useAppDispatch();

  const [clinics, setClinics] = useState<ClinicResponse[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    values,
    dirty,
    handleChange,
    setFieldValue,
    handleSubmit,
    errors,
    resetForm,
    submitCount,
  } = useFormik<RoentgenBase>({
    initialValues: {
      name: initialValues ? initialValues.name : "",
      clinic: initialValues ? initialValues.clinic._id : "",
    },
    onSubmit: (values) => {
      axios
        .put(
          "/classifiers/roentgens",
          initialValues ? { _id: initialValues._id, ...values } : values
        )
        .then(() => {
          callback();
          handleClose();
        })
        .catch((err) => {
          console.log("err:", err);
        });
    },
    validationSchema: roentgenSchema,
  });

  useEffect(() => {
    axios
      .get<ClinicResponse[]>("/clinics")
      .then((res) => {
        setClinics(res.data);
      })
      .catch((err) => {
        console.log("err:", err);
      });
  }, []);

  const handleDelete = (id: string) => {
    axios
      .delete(`/classifiers/roentgens/${id}`)
      .then(() => {
        callback();
        handleClose();
      })
      .catch((err) => {
        console.log("err:", err);
        setIsDeleting(false);
      });
  };

  const handleClose = () => {
    dispatch(closeModalById(id));
  };

  return (
    <Dialog fullWidth={true} maxWidth="xs" open={true} onClose={handleClose}>
      <DialogTitle>{"_id" in values ? "Labot" : "Pievienot"} rentgenu</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <TextField
            id="name"
            label="Nosaukums"
            variant="standard"
            value={values.name}
            onChange={handleChange}
            error={Boolean(errors.name) && submitCount > 0}
          />
          <Autocomplete
            options={clinics}
            getOptionLabel={(option) => option.name}
            value={clinics.find((clinic) => values.clinic === clinic._id) || null}
            onChange={(event: any, newValue: ClinicResponse | null) => {
              setFieldValue("clinic", newValue ? newValue._id : null);
            }}
            disableCloseOnSelect
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label="Klīnika"
                error={Boolean(errors.clinic) && submitCount > 0}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <PermissionGuard permission={PERMISSIONS.DELETE_ROENTGENS}>
          {({ hasAccess }) => (
            <>
              {hasAccess && initialValues ? (
                <LoadingButton
                  loading={isDeleting}
                  onClick={() => {
                    setIsDeleting(true);
                    dispatch(
                      openModal({
                        type: MODALS.Approve,
                        title: "Vai tiešām dzēst rentgenu?",
                        message: "Rentgens tiks dzēsts neatgriezeniski.",
                        onApprove: () => {
                          handleDelete(initialValues._id);
                        },
                        onReject: () => {
                          setIsDeleting(false);
                        },
                      })
                    );
                  }}
                  color="error"
                  variant="outlined"
                >
                  Dzēst
                </LoadingButton>
              ) : null}
            </>
          )}
        </PermissionGuard>
        <Stack direction="row" spacing={2} justifyContent="flex-end" flex={1}>
          <Button onClick={handleClose}>Aizvērt</Button>
          <Button
            disabled={isDeleting}
            onClick={() => {
              handleSubmit();
            }}
            variant="contained"
          >
            {initialValues ? "Saglabāt" : "Pievienot"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default RoentgenModal;
